'use client';

import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { GET_ACH_DISCOUNT_ELIGIBILITY, GET_CREDIT_CARD_FEE_PRICE, PAY_INVOICE_PROCESS_PAYMENT } from '../../../queries';
import { ProcessSlidePayPaymentProps } from '../../../types';
import { convertUnixMsToDateInTimezone, datagridFilterPanelAttributes } from '../../../utils';
import { CurrencyInputChangeEvent } from '../../inputs';
import { AchDiscountNotice } from '../achDiscountNotice';
import { CreditCardFeeDisclaimer } from '../creditCardFeeDisclaimer';
import { SlidePayCreditCardForm } from './slidePayCreditCardForm';
import { slidePayInvoiceTableColumns } from './slidePayInvoiceTableConfig';

type Invoice = {
	id: string;
	readable_id: string;
	created_at: string;
	amount: number;
	balance: number;
	payment: string | number;
	selected: boolean;
};

export const ProcessSlidePayPayment: FC<ProcessSlidePayPaymentProps> = ({
	adminProcessPayments,
	appName,
	billingProfileId,
	cognitoSub,
	editPaymentOptions,
	invoiceData,
	invoiceLoading,
	profileData,
	subscriptionId,
	useCreditCard,
	useAch,
	tenantSubscriptionId,
	setShowSnackbar,
	refetchInvoices,
	refetchPaymentMethods,
}) => {
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
	const [showNewCardFields, setShowNewCardFields] = useState(false);
	const [selectedToken, setSelectedToken] = useState('');
	const [creditCardFee, setCreditCardFee] = useState(0);
	const [paymentMethodType, setPaymentMethodType] = useState<'card' | 'ach'>();
	const [isEligibleForAchDiscount, setIsEligibleForAchDiscount] = useState(false);
	const [achDiscountPercentage, setAchDiscountPercentage] = useState(0);
	const [achDiscountAmount, setAchDiscountAmount] = useState(0);

	const cards = useMemo(() => profileData.gpiCards, [profileData]);
	const bankAccounts = useMemo(() => profileData.gpiBankAccounts, [profileData]);

	const showCreditCardFeeDisclaimer = useMemo(
		() =>
			selectedToken &&
			selectedInvoice &&
			cards?.some((card) => card.token === selectedToken) &&
			creditCardFee !== null,
		[cards, creditCardFee, selectedInvoice, selectedToken],
	);

	const showAchDiscountNotice = useMemo(
		() =>
			selectedInvoice &&
			selectedToken &&
			bankAccounts.some((account) => account.token === selectedToken) &&
			achDiscountAmount > 0 &&
			Math.round(Number(selectedInvoice.payment) * 100) === Math.round(Number(selectedInvoice.amount) * 100),
		[achDiscountAmount, bankAccounts, selectedInvoice, selectedToken],
	);

	const paymentAmount = useMemo(() => {
		if (!selectedInvoice) {
			return 0;
		}

		return Number(selectedInvoice.payment);
	}, [selectedInvoice]);

	const { totalAmount, totalBalance, totalPayment } = useMemo(() => {
		const amount = invoices.reduce((sum, row) => sum + Number(row.amount || '0'), 0);
		const balance = invoices.reduce((sum, row) => sum + Number(row.balance || '0'), 0);

		return { totalAmount: amount, totalBalance: balance, totalPayment: paymentAmount };
	}, [invoices, paymentAmount]);

	const totalPaymentAmount = useMemo(() => {
		if (showCreditCardFeeDisclaimer && creditCardFee > 0) {
			return (paymentAmount + creditCardFee).toFixed(2);
		}

		if (showAchDiscountNotice && achDiscountAmount > 0) {
			return (paymentAmount - achDiscountAmount).toFixed(2);
		}

		return paymentAmount.toFixed(2);
	}, [achDiscountAmount, creditCardFee, paymentAmount, showCreditCardFeeDisclaimer, showAchDiscountNotice]);

	const newCardButtonText = useMemo(() => {
		if (showNewCardFields) {
			return 'Use an existing card';
		}

		if (cards?.length > 0) {
			return 'Use a different card';
		}

		return 'Add a new card';
	}, [cards, showNewCardFields]);

	const [getCreditCardFeePrice, { loading: creditCardFeeLoading, error: creditCardFeeError }] = useLazyQuery(
		GET_CREDIT_CARD_FEE_PRICE,
		{
			context: { apiVersion: 'v2' },
			fetchPolicy: 'network-only',
			onCompleted: (data) => {
				const { price, displayPrice } = data.getCreditCardFeePrice;
				const fee = price > 0 ? displayPrice : null;
				setCreditCardFee(Number(fee));
			},
		},
	);

	const [getAchDiscountEligibility, { loading: achDiscountEligibilityLoading }] = useLazyQuery(
		GET_ACH_DISCOUNT_ELIGIBILITY,
		{
			context: { apiVersion: 'v2' },
			onCompleted: ({ getAchDiscountEligibility: response }) => {
				const { isEligible, discountPercentage, totalDiscountAmount } = response;

				setIsEligibleForAchDiscount(isEligible);
				setAchDiscountPercentage(discountPercentage);
				setAchDiscountAmount(totalDiscountAmount / 100);
			},
		},
	);

	const [payInvoice, { loading: isPayingInvoice }] = useMutation(PAY_INVOICE_PROCESS_PAYMENT, {
		context: {
			apiVersion: 'v2',
			appName,
		},
		onCompleted: () => {
			setShowSnackbar({
				message: 'The payment has been processed',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
					setSelectedInvoice(null);
				},
			});

			refetchInvoices();
		},
		onError: (err) => {
			setShowSnackbar({
				message: `Payment Failed - ${err.message}`,
				severity: 'error',
			});
		},
	});

	useEffect(() => {
		if (!invoiceData) {
			return;
		}

		setInvoices(
			invoiceData.map((invoice) => ({
				id: invoice.id,
				readable_id: invoice.readable_id || invoice.readableId,
				created_at: convertUnixMsToDateInTimezone(invoice.created_at || invoice.createdAt),
				amount: invoice.amount / 100,
				balance: invoice.balance / 100,
				payment: (invoice.balance / 100).toFixed(2),
				selected: false,
			})),
		);
	}, [invoiceData]);

	useEffect(() => {
		if (!selectedInvoice || !selectedToken) {
			return;
		}
		if (paymentMethodType === 'card') {
			getCreditCardFeePrice({
				variables: {
					billingProfileId,
					paymentAmount: Math.round(totalPayment * 100),
					numberOfInvoices: 1,
					subscriptionId,
				},
			});
		}

		if (paymentMethodType === 'ach') {
			getAchDiscountEligibility({
				variables: {
					subscriptionId,
					invoiceIds: [selectedInvoice.id],
				},
			});
		}
	}, [
		billingProfileId,
		getAchDiscountEligibility,
		getCreditCardFeePrice,
		selectedInvoice,
		selectedToken,
		subscriptionId,
		totalPayment,
		paymentMethodType,
	]);

	const onCardSelected = useCallback(
		(token: string) => {
			setSelectedToken(token);
			setPaymentMethodType('card');
		},
		[setSelectedToken],
	);

	const onBankAccountSelected = useCallback(
		(token: string) => {
			setSelectedToken(token);
			setPaymentMethodType('ach');
			setCreditCardFee(null);
		},
		[setSelectedToken],
	);

	const onCardSaved = useCallback(
		({ token }: { token: string }) => {
			setShowNewCardFields(false);
			onCardSelected(token);
		},
		[onCardSelected],
	);

	const onInvoiceSelected = useCallback(
		(invoiceId: string) => {
			const updatedInvoices = invoices.map((inv) => ({
				...inv,
				selected: inv.id === invoiceId,
				payment: inv.id === invoiceId ? inv.payment : inv.balance.toFixed(2),
			}));

			const invoice = updatedInvoices.find((i) => i.id === invoiceId);

			setInvoices(updatedInvoices);
			setSelectedInvoice(invoice);
		},
		[setSelectedInvoice, setInvoices, invoices],
	);

	const handlePaymentAmountChange = useCallback(
		(event: CurrencyInputChangeEvent, rowId: string) => {
			const inputValue = event.target.value;

			setInvoices((currentInvoices) => {
				const updatedInvoices = currentInvoices.map((invoice) =>
					invoice.id === rowId ? { ...invoice, payment: inputValue } : invoice,
				);
				const updatedInvoice = updatedInvoices.find((invoice) => invoice.id === rowId);

				if (selectedInvoice?.id === rowId && updatedInvoice) {
					setSelectedInvoice(updatedInvoice);
				}

				return updatedInvoices;
			});
		},
		[selectedInvoice],
	);

	const makePayment = useCallback(() => {
		if (!selectedInvoice || !selectedToken) {
			return;
		}

		const paymentAmount = Math.round(Number(selectedInvoice.payment) * 100);
		const invoiceBalance = Math.round(Number(selectedInvoice.balance) * 100);
		const isValidPayment = paymentAmount >= 0 && paymentAmount <= invoiceBalance;

		if (!isValidPayment) {
			setShowSnackbar({
				message: 'Invalid payment amount',
				severity: 'error',
				autoHideDuration: 5000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});

			return;
		}

		const isFullPayment = invoiceBalance === paymentAmount;

		payInvoice({
			variables: {
				invoiceId: selectedInvoice.id,
				paymentToken: selectedToken,
				paymentMethod: paymentMethodType,
				tenantSubscriptionId,
				...(!isFullPayment && { customPaymentAmount: paymentAmount }),
			},
		});
	}, [selectedInvoice, selectedToken, payInvoice, paymentMethodType, tenantSubscriptionId, setShowSnackbar]);

	const disableEdit = adminProcessPayments === 'DISPLAY_AND_VIEW_ONLY';
	const disableAddCard = editPaymentOptions === 'DISPLAY_AND_VIEW_ONLY';

	const noInvoice = !invoiceLoading && invoices.length === 0;

	const totalRow = useMemo(
		() => ({
			id: 'total',
			readable_id: 'Total',
			created_at: '',
			amount: totalAmount,
			balance: totalBalance,
			payment: totalPaymentAmount,
		}),
		[totalAmount, totalBalance, totalPaymentAmount],
	);

	const columns = slidePayInvoiceTableColumns(disableEdit, onInvoiceSelected, handlePaymentAmountChange);

	return (
		<Box className='pca___subscription-process-payment' sx={{ padding: 3, marginTop: -1 }}>
			{noInvoice && (
				<Alert
					severity='info'
					sx={{ color: 'black' }}
				>{`All of this subscription's invoices have been paid`}</Alert>
			)}
			<DataGridPremium
				className='invoice-table'
				columns={columns}
				rows={[...invoices, totalRow]}
				loading={invoiceLoading}
				getRowClassName={(params) => (params.id === 'total' ? 'total-row' : '')}
				getRowHeight={(params) => (params.id === 'total' ? 50 : 40)}
				autoHeight
				disableRowSelectionOnClick
				disableColumnReorder
				disableColumnMenu
				hideFooter
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
			/>
			{!disableEdit && (
				<Box sx={{ mt: 5 }}>
					<h3>Payment Options</h3>
					<Grid container spacing={2} sx={{ mt: 1 }}>
						{useCreditCard && (
							<Grid item xs={5} sm={6}>
								<h4>Credit Card</h4>
								<Box
									sx={{
										mt: 1,
										mb: 1,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start',
										width: '30%',
									}}
								>
									{cards?.map((card) => {
										const expMonth = card.exp_month || card.expMonth;
										const expYear = card.exp_year || card.expYear;

										return (
											<FormControlLabel
												key={card.token}
												control={
													<Checkbox
														size='small'
														checked={selectedToken === card.token}
														onChange={() => onCardSelected(card.token)}
														disabled={showNewCardFields}
													/>
												}
												label={
													<Typography variant='body2'>
														{`${card.type} ending in ${card.last4}, ${expMonth}/${expYear}`}
													</Typography>
												}
											/>
										);
									})}

									{!disableAddCard && (
										<Button
											variant='outlined'
											size='small'
											sx={{ mt: 3, mb: 3 }}
											onClick={() => setShowNewCardFields(!showNewCardFields)}
										>
											{newCardButtonText}
										</Button>
									)}
								</Box>
							</Grid>
						)}
						{useAch && (
							<Grid item xs={5} sm={6}>
								<h4>Bank Account</h4>
								<Box
									sx={{
										mt: 1,
										mb: 1,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start',
										width: '30%',
									}}
								>
									{bankAccounts?.map((account) => (
										<FormControlLabel
											key={account.token}
											control={
												<Checkbox
													size='small'
													checked={selectedToken === account.token}
													onChange={() => onBankAccountSelected(account.token)}
													data-cy='bank-account-checkbox'
												/>
											}
											label={
												<Typography variant='body2'>
													{`${account.accountType} account ending in ${account.last4}`}
												</Typography>
											}
										/>
									))}
									{showAchDiscountNotice && (
										<AchDiscountNotice
											isEligible={isEligibleForAchDiscount}
											isLoading={achDiscountEligibilityLoading}
											discountPercentage={achDiscountPercentage}
										/>
									)}
								</Box>
							</Grid>
						)}
					</Grid>
					{showCreditCardFeeDisclaimer && (
						<CreditCardFeeDisclaimer
							creditCardFee={creditCardFee.toFixed(2)}
							creditCardFeeLoading={creditCardFeeLoading}
							creditCardFeeError={creditCardFeeError}
						/>
					)}
					{!showNewCardFields && (
						<Button
							size='small'
							variant='contained'
							sx={{ mt: 3 }}
							onClick={makePayment}
							disabled={!selectedToken || !selectedInvoice || creditCardFeeLoading || isPayingInvoice}
						>
							{isPayingInvoice ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Make Payment'}
						</Button>
					)}
				</Box>
			)}
			{!disableEdit && showNewCardFields && (
				<Box>
					<SlidePayCreditCardForm
						cognitoSub={cognitoSub}
						subscriptionId={subscriptionId}
						refetchPaymentMethods={refetchPaymentMethods}
						setShowSnackbar={setShowSnackbar}
						onCardSaved={onCardSaved}
					/>
				</Box>
			)}
		</Box>
	);
};
